import type {
  Consent,
  Incident,
  IncidentCreate,
  IncidentUpdate,
  RetentionPolicy,
  RetentionPolicyUpdate,
  SubjectRequest,
  SubjectRequestCreated,
  SubjectRequestType,
  SubjectRequestUpdate,
} from "@/types/lgpd";
import type {
  PiiAccessLog,
  PiiAccessLogQuery,
  RetentionPolicyCreate,
} from "@/types/pii-access-log";

import { api, unwrap } from "./axiosConfig";

export const lgpdService = {
  // ─── Self-service consents ─────────────────────────────────────────
  listConsents(): Promise<Consent[]> {
    return unwrap(api.get("/api/v1/me/consents"));
  },
  revokeConsent(purpose: string): Promise<void> {
    return api.post(`/api/v1/me/consents/${purpose}/revoke`).then(() => undefined);
  },

  // ─── Public: data-subject request (Art. 18) ────────────────────────
  submitRequest(payload: {
    email: string;
    type: SubjectRequestType;
    description?: string;
  }): Promise<SubjectRequestCreated> {
    return unwrap(api.post("/api/v1/lgpd/requests", payload));
  },

  // ─── Admin: subject requests ───────────────────────────────────────
  listRequests(params?: { status?: string; limit?: number }): Promise<SubjectRequest[]> {
    return unwrap(api.get("/api/v1/admin/lgpd/requests", { params }));
  },
  getRequest(id: number): Promise<SubjectRequest> {
    return unwrap(api.get(`/api/v1/admin/lgpd/requests/${id}`));
  },
  updateRequest(id: number, payload: SubjectRequestUpdate): Promise<SubjectRequest> {
    return unwrap(api.patch(`/api/v1/admin/lgpd/requests/${id}`, payload));
  },

  // ─── Admin: incidents ──────────────────────────────────────────────
  listIncidents(params?: { status?: string; limit?: number }): Promise<Incident[]> {
    return unwrap(api.get("/api/v1/admin/lgpd/incidents", { params }));
  },
  createIncident(payload: IncidentCreate): Promise<Incident> {
    return unwrap(api.post("/api/v1/admin/lgpd/incidents", payload));
  },
  updateIncident(id: number, payload: IncidentUpdate): Promise<Incident> {
    return unwrap(api.patch(`/api/v1/admin/lgpd/incidents/${id}`, payload));
  },

  // ─── Admin: retention policies ─────────────────────────────────────
  listRetentionPolicies(): Promise<RetentionPolicy[]> {
    return unwrap(api.get("/api/v1/admin/lgpd/retention-policies"));
  },
  createRetentionPolicy(payload: RetentionPolicyCreate): Promise<RetentionPolicy> {
    return unwrap(api.post("/api/v1/admin/lgpd/retention-policies", payload));
  },
  updateRetentionPolicy(id: number, payload: RetentionPolicyUpdate): Promise<RetentionPolicy> {
    return unwrap(api.patch(`/api/v1/admin/lgpd/retention-policies/${id}`, payload));
  },

  // ─── PII access logs (Art. 37 trail) ───────────────────────────────
  // Workspace-scoped (gated por lgpd.pii_access_logs.read). O X-Workspace-Id
  // já vai no header; o backend monta a rota sob /workspaces/{id}.
  listPiiAccessLogs(workspaceId: number, params?: PiiAccessLogQuery): Promise<PiiAccessLog[]> {
    return unwrap(
      api.get(`/api/v1/workspaces/${workspaceId}/pii-access-logs`, { params }),
    );
  },
};
